import React, { useState } from 'react';
import ScrollFade from './ScrollFade';
import { Activity, Project } from '../types';
import { FloatThresholds, getFloatTier, getFloatColor } from '../utils/riskUtils';
import { fmtShort, statusBadgeClass, computeStatus } from './WeeklyGrid';

export interface FloatPathGridProps {
  activities: Activity[];
  thresholds?: FloatThresholds;
  project?: Project;
}

const NO_PATH_KEY = '__none__';

interface FloatPathGroup {
  key: string;
  label: string;
  floatPath: number | null;
  activities: Activity[];
}

function groupTier(groupActivities: Activity[], thresholds: FloatThresholds | undefined): 'Critical' | 'Watch' | 'Healthy' {
  if (groupActivities.some(a => a.is_critical)) return 'Critical';
  const worst = Math.min(...groupActivities.map(a => Number(a.total_float ?? 0)));
  return getFloatTier(worst, thresholds);
}

const FloatPathGrid: React.FC<FloatPathGridProps> = ({ activities, thresholds }) => {
  const [searchText, setSearchText] = useState('');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const toggleGroup = (key: string) => {
    setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // ── Group by float_path ──────────────────────────────────────────────────
  const byPath = new Map<string, Activity[]>();
  for (const a of activities) {
    const key = a.float_path == null || a.float_path === undefined || (a.float_path as any) === ''
      ? NO_PATH_KEY
      : String(a.float_path);
    if (!byPath.has(key)) byPath.set(key, []);
    byPath.get(key)!.push(a);
  }

  const groups: FloatPathGroup[] = Array.from(byPath.entries())
    .map(([key, groupActivities]) => ({
      key,
      label: key === NO_PATH_KEY ? 'No float path' : `Float path ${key}`,
      floatPath: key === NO_PATH_KEY ? null : Number(key),
      activities: [...groupActivities].sort((a, b) => Number(a.float_path_order ?? 0) - Number(b.float_path_order ?? 0)),
    }))
    .sort((a, b) => {
      if (a.floatPath === null) return 1;
      if (b.floatPath === null) return -1;
      return a.floatPath - b.floatPath;
    });

  // ── Search filter — hides non-matching activities, keeps group header if any match ──
  const q = searchText.trim().toLowerCase();
  const visibleGroups = groups
    .map(g => ({
      ...g,
      activities: q
        ? g.activities.filter(a => a.activity_name.toLowerCase().includes(q) || a.activity_id.toLowerCase().includes(q))
        : g.activities,
    }))
    .filter(g => g.activities.length > 0);

  return (
    <div>
      {/* Compact search bar — top-right */}
      <div className="px-3 sm:px-4 py-3 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 flex justify-end">
        <div className="relative w-full sm:w-[220px]">
          <input
            type="text"
            placeholder="Search activity name or ID..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            className="w-full border border-gray-200 dark:border-gray-600 rounded-md px-3 py-1.5 pr-6 text-xs bg-white dark:bg-gray-800 text-text dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent tap"
          />
          {searchText && (
            <button
              onClick={() => setSearchText('')}
              className="absolute right-0 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-muted dark:text-gray-400 hover:text-text dark:hover:text-gray-200 text-base leading-none"
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {visibleGroups.length === 0 ? (
        <div className="text-center py-16 px-4">
          <div className="text-5xl mb-3">🔀</div>
          <p className="text-base font-semibold text-text dark:text-gray-100">No activities match your search</p>
          <p className="text-sm text-muted dark:text-gray-400 mt-1">Try a different activity name or ID.</p>
        </div>
      ) : (
        <ScrollFade>
        <div className="overflow-auto overscroll-x-contain" style={{ maxHeight: 'calc(100dvh - 200px)' }}>
          <table className="border-collapse bg-white dark:bg-gray-900 w-full min-w-[700px] md:min-w-[860px]">
            <thead className="sticky top-0 z-20 border-b-2 border-gray-200 dark:border-gray-700">
              <tr className="divide-x divide-gray-100 dark:divide-gray-700">
                <th className="th-flo sticky left-0 z-30 dark:bg-gray-800 text-left py-2.5 px-2 sm:px-3 w-[80px] min-w-[80px] max-w-[80px] sm:w-[100px] sm:min-w-[100px] sm:max-w-[100px]">
                  Activity ID
                </th>
                <th className="th-flo sticky left-[80px] sm:left-[100px] z-30 text-left py-2.5 px-2 dark:bg-gray-800 min-w-[150px] max-w-[150px] sm:min-w-[220px] sm:max-w-none shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)]">
                  Activity name
                </th>
                <th className="th-flo text-center py-2.5 px-2 dark:bg-gray-800 w-[110px]">
                  Start
                </th>
                <th className="th-flo text-center py-2.5 px-2 dark:bg-gray-800 w-[110px]">
                  Finish
                </th>
                <th className="th-flo text-center py-2.5 px-2 dark:bg-gray-800 w-[90px]">
                  Status
                </th>
                <th className="th-flo text-center py-2.5 px-2 dark:bg-gray-800 w-[70px]">
                  Float Days
                </th>
                <th className="th-flo text-left py-2.5 px-2 dark:bg-gray-800" style={{ minWidth: 160 }}>
                  Predecessors
                </th>
                <th className="th-flo text-left py-2.5 px-2 dark:bg-gray-800" style={{ minWidth: 160 }}>
                  Successors
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleGroups.map(group => {
                const tier = groupTier(group.activities, thresholds);
                const color = getFloatColor(tier);
                const worstFloat = Math.min(...group.activities.map(a => Number(a.total_float ?? 0)));
                const isExpanded = !collapsed[group.key];
                return (
                  <React.Fragment key={group.key}>
                    <tr onClick={() => toggleGroup(group.key)} className="cursor-pointer">
                      <td colSpan={8} className="py-2 px-3 bg-ink">
                        <div className="sticky left-3 inline-flex items-center gap-2 min-h-[28px]">
                          <svg
                            className={`w-3 h-3 shrink-0 text-white transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`}
                            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                          <span className="text-xs font-bold text-white uppercase tracking-wider">
                            {group.label} · {group.activities.length} {group.activities.length === 1 ? 'activity' : 'activities'}
                          </span>
                          <span
                            style={{ backgroundColor: color.bg, color: color.text }}
                            className="ml-1 pill"
                          >
                            {worstFloat}d
                          </span>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && group.activities.map(a => {
                      const status = computeStatus(a.actual_start ?? '', a.actual_finish ?? '');
                      return (
                        <tr key={a.activity_id} className="border-b border-gray-100 dark:border-gray-800 divide-x divide-gray-100 dark:divide-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/40">
                          <td className="sticky left-0 z-10 bg-white dark:bg-gray-900 py-2 px-2 sm:px-3 text-xs font-mono text-muted dark:text-gray-400 break-all w-[80px] min-w-[80px] max-w-[80px] sm:w-[100px] sm:min-w-[100px] sm:max-w-[100px]">
                            {a.activity_id}
                          </td>
                          <td className="sticky left-[80px] sm:left-[100px] z-10 bg-white dark:bg-gray-900 py-2 px-2 text-sm text-text dark:text-gray-100 min-w-[150px] max-w-[150px] sm:min-w-[220px] sm:max-w-none shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)]">
                            {a.activity_name}
                          </td>
                          <td className="text-center py-2 px-2 text-xs text-muted dark:text-gray-400 whitespace-nowrap">
                            {fmtShort(a.start_date)}
                          </td>
                          <td className="text-center py-2 px-2 text-xs text-muted dark:text-gray-400 whitespace-nowrap">
                            {fmtShort(a.finish_date)}
                          </td>
                          <td className="text-center py-2 px-2">
                            <span className={`pill ${statusBadgeClass(status)}`}>
                              {status}
                            </span>
                          </td>
                          <td className="text-center py-2 px-2 text-xs font-semibold text-muted dark:text-gray-400">
                            {a.total_float != null ? a.total_float : '—'}
                          </td>
                          <td className="py-2 px-2 text-xs text-muted dark:text-gray-400">
                            {a.predecessors_display || '—'}
                          </td>
                          <td className="py-2 px-2 text-xs text-muted dark:text-gray-400">
                            {a.successors || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        </ScrollFade>
      )}
    </div>
  );
};

export default FloatPathGrid;
